import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Book, GraduationCap, GitFork, HelpCircle, Calendar, ClipboardList, FileText, Tag, PartyPopper, Award } from 'lucide-react';
import { getAllContentManagementFilterCounts } from '../../utils/contentManagementFilters';

const ContentManagementSidebar = ({ activeTab, onTabChange, isAdmin, isInstructor }) => {
  const navigate = useNavigate();
  const [filterCounts, setFilterCounts] = useState(() => getAllContentManagementFilterCounts());
  const allTabs = [
    { id: 'Lessons', icon: Book, label: 'Lessons' },
    { id: 'Courses', icon: GraduationCap, label: 'Courses' },
    { id: 'Paths', icon: GitFork, label: 'Paths' },
    { id: 'Questions', icon: HelpCircle, label: 'Questions' },
    { id: 'Events & Information', icon: Calendar, label: 'Events & Information' },
    { id: 'Submission Forms', icon: ClipboardList, label: 'Submission Forms' },
    { id: 'Submissions', icon: FileText, label: 'Submissions' },
    { id: 'Reinforcements', icon: PartyPopper, label: 'Reinforcements' },
    { id: 'Tags', icon: Tag, label: 'Tags' },
    { id: 'Badges', icon: Award, label: 'Badges' }
  ];

  // Filter tabs based on role
  const tabs = allTabs.filter(tab => {
    if (isAdmin) return true; // Admins see all tabs
    if (isInstructor) {
      // Instructors can't see Questions, Submission Forms, Submissions, and Tags
      return !['Questions', 'Submission Forms', 'Submissions', 'Tags', 'Badges'].includes(tab.id);
    }
    return false;
  });

  const handleTabClick = (tabId) => {
    if (tabId === 'Lessons') {
      navigate('/content-management/lessons');
    } else if (tabId === 'Courses') {
      navigate('/content-management/courses');
    } else if (tabId === 'Paths') {
      navigate('/content-management/paths');
    } else if (tabId === 'Events & Information') {
      navigate('/content-management/events');
    } else if (tabId === 'Submission Forms') {
      navigate('/content-management/submission-forms');
    } else if (tabId === 'Submissions') {
      navigate('/content-management/submissions');
    } else if (tabId === 'Questions') {
      navigate('/content-management/questions');
    } else if (tabId === 'Reinforcements') {
      navigate('/content-management/reinforcements');
    } else if (tabId === 'Tags') {
      navigate('/content-management/tags');
    } else if (tabId === 'Badges') {
      navigate('/content-management/badges');
    } else {
      onTabChange(tabId);
    }
  };

  useEffect(() => {
    const updateCounts = () => {
      setFilterCounts(getAllContentManagementFilterCounts());
    };

    updateCounts();
    window.addEventListener('contentManagementFiltersUpdated', updateCounts);
    window.addEventListener('storage', updateCounts);
    return () => {
      window.removeEventListener('contentManagementFiltersUpdated', updateCounts);
      window.removeEventListener('storage', updateCounts);
    };
  }, []);

  return (
    <aside className="w-full md:w-64 bg-gray-50 p-4 sm:p-6 rounded-t-lg md:rounded-l-lg md:rounded-tr-none">
      <div className="flex md:block items-center justify-between mb-4 md:mb-8">
        <h2 className="text-xl font-bold text-[#054581] hidden md:block">Content Management</h2>
      </div>
      {/* Mobile Navigation */}
      <nav className="md:hidden grid grid-cols-2 sm:grid-cols-4 gap-1">
        {tabs.map(({ id, icon: Icon, label }) => {
          const count = filterCounts[id] || 0;
          return (
          <button
            key={id}
            className={`flex flex-col items-center justify-center p-2 rounded-lg font-medium transition-colors duration-150 ${
              activeTab === id
                ? 'bg-[#1086a4] text-white'
                : 'text-[#054581] hover:bg-[#1086a4]/10 hover:text-[#1086a4]'
            }`}
            onClick={() => handleTabClick(id)}
          >
            <Icon className="w-6 h-6 mb-1 stroke-[1.5]" />
            <span className="text-xs text-center flex items-center gap-1">
              {label}
              {count > 0 && (
                <span className={`px-1.5 py-0.5 text-[10px] rounded-full ${
                  activeTab === id ? 'bg-white text-[#1086a4]' : 'bg-[#1086a4] text-white'
                }`}>
                  {count}
                </span>
              )}
            </span>
          </button>
        );
        })}
      </nav>
      {/* Desktop Navigation */}
      <nav className="hidden md:flex md:flex-col gap-2 space-y-2">
        {tabs.map(({ id, icon: Icon, label }) => {
          const count = filterCounts[id] || 0;
          return (
          <button
            key={id}
            className={`flex items-center gap-3 text-left px-4 py-3 rounded-lg font-medium transition-colors duration-150 ${
              activeTab === id
                ? 'bg-[#1086a4] text-white shadow-inner'
                : 'text-[#054581] hover:bg-[#1086a4]/10 hover:text-[#1086a4]'
            }`}
            onClick={() => handleTabClick(id)}
          >
            <Icon className="w-5 h-5 stroke-[1.5]" />
            <span className="flex items-center gap-2">
              {label}
              {count > 0 && (
                <span className={`px-2 py-0.5 text-xs rounded-full ${
                  activeTab === id ? 'bg-white text-[#1086a4]' : 'bg-[#1086a4] text-white'
                }`}>
                  {count}
                </span>
              )}
            </span>
          </button>
        );
        })}
      </nav>
    </aside>
  );
};

export default ContentManagementSidebar; 